import { FLOSS, flossByCode, type FlossColor, type HueFamily } from '../data/dmc';
import type { BraceletStyle, MotifKind } from '../engine/types';
import { isRenderable, tokenize } from '../engine/font';
import { DEFAULT_SIZING, type DesignBrief } from './types';
import type { Sizing } from '../engine/types';

/**
 * The no-AI brief interpreter.
 *
 * This is the DEFAULT path, not a degraded one. With no API key at all, typing
 * "something like a sunset" has to produce a real sunset palette, and it does:
 * a curated theme table plus direct colour-word matching, both operating on the
 * same modest DMC palette the rest of the app uses.
 *
 * It is deliberately a lookup, not a model. It is inspectable, instant, offline,
 * and its failure mode is "I did not recognise that word", which the UI can say
 * out loud, rather than a confident hallucination.
 */

interface Theme {
  id: string;
  /** Words that select this theme. Matched as whole words or substrings. */
  keywords: string[];
  /** DMC codes, lead colour first. */
  colors: string[];
  mood: string;
  /** Style this theme suits best, if any. */
  leaning?: BraceletStyle;
  motifs?: MotifKind[];
}

const THEMES: Theme[] = [
  {
    id: 'sunset',
    keywords: ['sunset', 'sunrise', 'dusk', 'sundown', 'golden hour', 'evening sky'],
    colors: ['550', '602', '947', '741', '727'],
    mood: 'deep violet dropping through cranberry and burnt orange into a pale gold horizon',
  },
  {
    id: 'ocean',
    keywords: ['ocean', 'sea', 'wave', 'beach', 'marine', 'tide', 'nautical', 'water'],
    colors: ['820', '798', '995', '3843', '996'],
    mood: 'deep water shading up to bright shallows',
  },
  {
    id: 'forest',
    keywords: ['forest', 'woods', 'pine', 'moss', 'jungle', 'tree', 'leaf', 'leaves'],
    colors: ['699', '700', '505', '702', '907'],
    mood: 'layered greens from deep pine to new growth',
  },
  {
    id: 'cherry',
    keywords: ['cherry', 'cherries'],
    colors: ['498', '666', '702', '3865', '310'],
    mood: 'bright fruit on a green stem',
    leaning: 'chain',
    motifs: ['cherry'],
  },
  {
    id: 'strawberry',
    keywords: ['strawberry', 'strawberries', 'berry', 'berries'],
    colors: ['666', '444', '702', '3865', '699'],
    mood: 'red berries with seed flecks on green',
    leaning: 'chain',
    motifs: ['strawberry'],
  },
  {
    id: 'daisy',
    keywords: ['daisy', 'daisies', 'flower', 'flowers', 'floral', 'bloom', 'petal'],
    colors: ['3865', '444', '907', '818', '702'],
    mood: 'white petals with a yellow centre on a green chain',
    leaning: 'chain',
    motifs: ['daisy'],
  },
  {
    id: 'lavender',
    keywords: ['lavender', 'lilac', 'violet', 'wisteria'],
    colors: ['550', '552', '553', '3865', '505'],
    mood: 'soft purples against cream',
  },
  {
    id: 'candy',
    keywords: ['candy', 'sweet', 'bubblegum', 'sprinkle', 'lolly', 'birthday'],
    colors: ['3805', '996', '307', '907', 'BLANC'],
    mood: 'loud, cheerful sweet-shop colours',
  },
  {
    id: 'rainbow',
    keywords: ['rainbow', 'pride', 'spectrum', 'colourful', 'colorful', 'multicolour', 'multicolor'],
    colors: ['666', '740', '444', '702', '798', '550'],
    mood: 'a full spectrum, red through violet',
  },
  {
    id: 'pastel',
    keywords: ['pastel', 'soft', 'gentle', 'baby', 'muted', 'dreamy', 'cloud'],
    colors: ['818', '727', '553', '996', '3865'],
    mood: 'washed-out, gentle tones',
  },
  {
    id: 'neon',
    keywords: ['neon', 'bright', 'electric', 'vivid', 'highlighter', 'bold', 'loud'],
    colors: ['3805', '907', '307', '3843', '666'],
    mood: 'maximum saturation, no quiet colours',
  },
  {
    id: 'autumn',
    keywords: ['autumn', 'fall', 'harvest', 'pumpkin', 'cosy', 'cozy', 'october'],
    colors: ['433', '947', '741', '444', '699'],
    mood: 'warm browns and burnt oranges with a bite of green',
  },
  {
    id: 'winter',
    keywords: ['winter', 'snow', 'frost', 'ice', 'icy', 'cold', 'december'],
    colors: ['820', '798', '996', '415', 'B5200'],
    mood: 'cold blues and greys against white',
  },
  {
    id: 'earth',
    keywords: ['earth', 'desert', 'clay', 'terracotta', 'sand', 'natural', 'neutral', 'nude'],
    colors: ['433', '436', '437', '738', 'ECRU'],
    mood: 'quiet, sandy neutrals',
  },
  {
    id: 'monochrome',
    keywords: ['black and white', 'monochrome', 'greyscale', 'grayscale', 'minimal', 'simple'],
    colors: ['310', '413', '415', '3865', 'B5200'],
    mood: 'black through grey to white, nothing else',
  },
  {
    id: 'cream',
    keywords: ['cream', 'vanilla', 'ivory', 'oat', 'latte', 'milk'],
    colors: ['ECRU', '739', '738', '842', '436'],
    mood: 'warm creams and pale tans',
  },
  {
    id: 'rose',
    keywords: ['rose', 'blush', 'romantic', 'valentine', 'love', 'heart'],
    colors: ['498', '3805', '956', '818', '3865'],
    mood: 'deep rose fading to blush',
    motifs: ['heart'],
  },
  {
    id: 'mint',
    keywords: ['mint', 'fresh', 'spearmint', 'seafoam', 'sage'],
    colors: ['505', '907', '3846', '3865', '727'],
    mood: 'cool green with a fresh edge',
  },
  {
    id: 'storm',
    keywords: ['storm', 'thunder', 'moody', 'dark', 'night', 'midnight', 'goth'],
    colors: ['310', '820', '550', '413', '333'],
    mood: 'very dark blues and purples',
  },
  {
    id: 'peach',
    keywords: ['peach', 'apricot', 'coral', 'melon'],
    colors: ['947', '956', '3713', '727', '3865'],
    mood: 'warm coral into pale peach',
  },
];

/** Direct colour words, mapped to the closest thing in the palette. */
const COLOR_WORDS: { words: string[]; dmc: string }[] = [
  { words: ['black'], dmc: '310' },
  { words: ['white'], dmc: 'BLANC' },
  { words: ['grey', 'gray', 'silver'], dmc: '415' },
  { words: ['charcoal', 'slate'], dmc: '413' },
  { words: ['red', 'scarlet'], dmc: '666' },
  { words: ['crimson', 'burgundy', 'maroon', 'wine'], dmc: '498' },
  { words: ['pink'], dmc: '956' },
  { words: ['hot pink', 'magenta', 'fuchsia'], dmc: '3805' },
  { words: ['blush', 'pale pink', 'light pink'], dmc: '818' },
  { words: ['salmon'], dmc: '3713' },
  { words: ['orange'], dmc: '740' },
  { words: ['burnt orange', 'rust'], dmc: '947' },
  { words: ['yellow'], dmc: '307' },
  { words: ['gold', 'mustard', 'amber'], dmc: '444' },
  { words: ['butter', 'pale yellow', 'lemon'], dmc: '727' },
  { words: ['green'], dmc: '702' },
  { words: ['dark green', 'forest green', 'emerald'], dmc: '699' },
  { words: ['lime', 'chartreuse'], dmc: '907' },
  { words: ['teal', 'jade'], dmc: '505' },
  { words: ['turquoise', 'aqua', 'cyan'], dmc: '3846' },
  { words: ['blue'], dmc: '798' },
  { words: ['navy', 'dark blue', 'royal blue'], dmc: '820' },
  { words: ['sky blue', 'light blue', 'baby blue'], dmc: '996' },
  { words: ['purple'], dmc: '552' },
  { words: ['dark purple', 'plum', 'aubergine'], dmc: '550' },
  { words: ['brown', 'chocolate'], dmc: '433' },
  { words: ['tan', 'camel', 'caramel'], dmc: '436' },
  { words: ['beige', 'taupe'], dmc: '842' },
  { words: ['cream', 'ecru', 'off white', 'off-white'], dmc: 'ECRU' },
];

/** Words that ask for a particular construction. */
const STYLE_WORDS: { words: string[]; style: BraceletStyle }[] = [
  {
    words: ['chevron', 'v shape', 'arrow', 'stripe', 'stripes', 'striped', 'diamond', 'zigzag', 'candy stripe', 'diagonal'],
    style: 'normal',
  },
  {
    words: ['name', 'word', 'letters', 'text', 'says', 'spell', 'alpha', 'picture', 'logo', 'writing'],
    style: 'alpha',
  },
  {
    words: ['daisy chain', 'daisy', 'flower', 'flowers', 'cherry', 'cherries', 'strawberry', 'motif', 'chain'],
    style: 'chain',
  },
];

const MOTIF_WORDS: { words: string[]; motif: MotifKind }[] = [
  { words: ['daisy', 'daisies', 'flower', 'flowers', 'floral'], motif: 'daisy' },
  { words: ['cherry', 'cherries'], motif: 'cherry' },
  { words: ['strawberry', 'strawberries', 'berry', 'berries'], motif: 'strawberry' },
  { words: ['heart', 'hearts', 'love'], motif: 'heart' },
];

/* -------------------------------------------------------------------------- */

export interface HeuristicResult extends DesignBrief {
  /** Which theme matched, if any — surfaced so the UI can explain itself. */
  matchedTheme?: string;
  /** Colour words found directly in the text. */
  matchedColorWords: string[];
  /** True when nothing at all was recognised and we fell back to a default. */
  usedFallback: boolean;
}

export function interpretHeuristically(
  text: string,
  options: {
    sizing?: Sizing;
    /** Colours she picked by hand always win over anything inferred. */
    pinnedColors?: string[];
    stylePreference?: BraceletStyle | 'any';
  } = {},
): HeuristicResult {
  const lower = ` ${text.toLowerCase().replace(/[^a-z0-9<>+.!?-]+/g, ' ')} `;
  const sizing = options.sizing ?? DEFAULT_SIZING;

  // 1. Theme match. Longer keywords win so "hot pink" beats "pink".
  let bestTheme: Theme | undefined;
  let bestScore = 0;
  for (const theme of THEMES) {
    for (const kw of theme.keywords) {
      if (lower.includes(` ${kw} `) || lower.includes(kw)) {
        const score = kw.length;
        if (score > bestScore) {
          bestScore = score;
          bestTheme = theme;
        }
      }
    }
  }

  // 2. Explicit colour words.
  const matchedColorWords: string[] = [];
  const directColors: string[] = [];
  const sortedColorWords = COLOR_WORDS.flatMap((c) => c.words.map((w) => ({ w, dmc: c.dmc }))).sort(
    (a, b) => b.w.length - a.w.length,
  );
  const consumed: [number, number][] = [];
  for (const { w, dmc } of sortedColorWords) {
    const idx = lower.indexOf(` ${w} `) >= 0 ? lower.indexOf(` ${w} `) : lower.indexOf(w);
    if (idx < 0) continue;
    // Do not let "pink" also fire inside "hot pink".
    if (consumed.some(([s, e]) => idx >= s && idx < e)) continue;
    consumed.push([idx, idx + w.length + 1]);
    if (!directColors.includes(dmc)) {
      directColors.push(dmc);
      matchedColorWords.push(w);
    }
  }

  // 3. Style leaning.
  let stylePreference: BraceletStyle | 'any' = options.stylePreference ?? 'any';
  if (stylePreference === 'any') {
    let bestStyleScore = 0;
    for (const { words, style } of STYLE_WORDS) {
      for (const w of words) {
        if (lower.includes(w) && w.length > bestStyleScore) {
          bestStyleScore = w.length;
          stylePreference = style;
        }
      }
    }
  }

  // 4. Motifs.
  const motifs: MotifKind[] = [];
  for (const { words, motif } of MOTIF_WORDS) {
    if (words.some((w) => lower.includes(w)) && !motifs.includes(motif)) motifs.push(motif);
  }

  // 5. A word to spell.
  const word = extractQuotedWord(text);
  if (word && stylePreference === 'any') stylePreference = 'alpha';

  // 6. Assemble the palette.
  const pinned = (options.pinnedColors ?? []).filter((c) => !!flossByCode(c));
  let colors = dedupe([...pinned, ...directColors, ...(bestTheme?.colors ?? [])]);

  const usedFallback = colors.length === 0;
  if (usedFallback) {
    // Nothing recognised. Rather than inventing a mood, use a plainly-labelled
    // default and let the UI say so.
    colors = ['798', '3843', '727', '3865'];
  }

  // Pad to at least four colours so the generators have room to work, choosing
  // companions that are already in the palette and read well together.
  colors = padPalette(colors, 4);

  const mood = bestTheme?.mood ?? describePalette(colors);

  const parts: string[] = [];
  if (pinned.length) parts.push(`${pinned.length} colour${pinned.length === 1 ? '' : 's'} you picked`);
  if (matchedColorWords.length) parts.push(`colour words (${matchedColorWords.join(', ')})`);
  if (bestTheme) parts.push(`the "${bestTheme.id}" theme`);
  if (word) parts.push(`the word "${word}"`);

  const sourceNote = usedFallback
    ? 'No API key and nothing recognisable in your description, so this is a plain default palette — pick colours by hand below to steer it.'
    : `Matched offline from ${parts.join(' and ')}. No AI was used.`;

  return {
    text,
    colors,
    stylePreference,
    word,
    motifs: motifs.length ? motifs : (bestTheme?.motifs ?? []),
    mood,
    sizing,
    source: 'heuristic',
    sourceNote,
    matchedTheme: bestTheme?.id,
    matchedColorWords,
    usedFallback,
  };
}

/* -------------------------------------------------------------------------- */

/**
 * Pull out a word she wants spelled. Quotes are the reliable signal; failing
 * that, "that says NAME" / "with the word NAME" patterns.
 */
export function extractQuotedWord(text: string): string | undefined {
  const quoted = text.match(/["'“”‘’]([^"'“”‘’]{1,14})["'“”‘’]/);
  if (quoted?.[1] && isRenderable(quoted[1]) && tokenize(quoted[1]).length > 0) {
    return quoted[1].toUpperCase().trim();
  }
  const phrase = text.match(
    /\b(?:says?|saying|spell(?:ing|s)?|word|name|reads?)\b[:\s]+([A-Za-z0-9 <3+.!?-]{1,24})/i,
  );
  if (phrase?.[1]) {
    const candidate = trimAtConnector(phrase[1]);
    if (candidate && isRenderable(candidate)) return candidate.toUpperCase();
  }
  return undefined;
}

/**
 * "the word BFF in pink" must yield BFF, not "BFF IN PINK".
 *
 * The regex above cannot tell where the name stops and the rest of the sentence
 * starts, so cut at the first connector word. Getting this wrong is not
 * cosmetic: an extra word makes the bracelet three times longer and eats three
 * times the thread.
 */
const WORD_STOPPERS = new Set([
  'in', 'on', 'with', 'and', 'using', 'for', 'please', 'made', 'made of', 'of',
  'over', 'across', 'against', 'onto', 'plus', 'but', 'or', 'then', 'so',
  'background', 'letters', 'thread', 'floss', 'colours', 'colors', 'style',
]);

function trimAtConnector(raw: string): string {
  const words = raw.trim().split(/\s+/).filter(Boolean);
  const kept: string[] = [];
  for (const w of words) {
    if (WORD_STOPPERS.has(w.toLowerCase())) break;
    kept.push(w);
    // Alpha bracelets get long fast; more than two tokens is almost certainly
    // the rest of the sentence rather than the name she wants.
    if (kept.join(' ').length >= 10) break;
  }
  return kept.join(' ').trim();
}

function dedupe(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of list) {
    const key = c.toUpperCase();
    if (!seen.has(key) && flossByCode(c)) {
      seen.add(key);
      out.push(flossByCode(c)!.dmc);
    }
  }
  return out;
}

/**
 * Top up a short palette with colours that already sit near it, so a
 * single-colour request still yields a workable bracelet. Deterministic:
 * it walks the palette in a fixed order rather than picking randomly.
 */
function padPalette(colors: string[], min: number): string[] {
  const out = colors.slice();
  if (out.length >= min) return out;

  const have = out.map((c) => flossByCode(c)!);
  const wantFamilies: HueFamily[] = have.length
    ? [...new Set(have.map((f) => f.family))]
    : ['blue'];

  // First: other lightnesses of the families already present.
  const candidates: FlossColor[] = [
    ...FLOSS.filter((f) => wantFamilies.includes(f.family)),
    // Then a neutral, which flatters almost anything.
    ...FLOSS.filter((f) => f.family === 'neutral'),
    ...FLOSS,
  ];

  for (const f of candidates) {
    if (out.length >= min) break;
    if (out.some((c) => c.toUpperCase() === f.dmc.toUpperCase())) continue;
    // Avoid two near-identical lightnesses of the same family.
    if (out.some((c) => {
      const e = flossByCode(c)!;
      return e.family === f.family && Math.abs(e.light - f.light) < 15;
    })) {
      continue;
    }
    out.push(f.dmc);
  }

  // Last resort if the filters were too strict.
  for (const f of FLOSS) {
    if (out.length >= min) break;
    if (!out.some((c) => c.toUpperCase() === f.dmc.toUpperCase())) out.push(f.dmc);
  }
  return out;
}

function describePalette(colors: string[]): string {
  const names = colors
    .map((c) => flossByCode(c)?.name.split('—')[0]!.trim())
    .filter(Boolean)
    .slice(0, 3);
  return names.length ? names.join(', ').toLowerCase() : 'a simple palette';
}

/** Exported for tests and for the "why did it pick this?" UI. */
export function listThemes(): { id: string; keywords: string[]; colors: string[] }[] {
  return THEMES.map((t) => ({ id: t.id, keywords: t.keywords, colors: t.colors }));
}
